import { KLASS_STATUS } from '@app/model/Enums';
import { LabelProps } from '@patternfly/react-core';
// Configuration parameters for the application

// Extend the Window interface so TypeScript doesn't complain
declare global {
  interface Window {
    _env_?: {
      REACT_APP_COMMIT_HASH?: string;
      REACT_APP_VERSION?: string;
      REACT_APP_BUILD_DATE?: string;
      REACT_APP_ALLOCATION_SERVICE_HOST?: string;
      REACT_APP_BASE_CERTIFICATION_VERIFY_URL?: string;
      REACT_APP_KEYCLOAK_URL?: string;
      REACT_APP_KEYCLOAK_REALM?: string;
      REACT_APP_KEYCLOAK_CLIENT_ID?: string;
      REACT_APP_FEEDBACK_ENABLED?: string;
      REACT_APP_FEEDBACK_REPO_OWNER?: string;
      REACT_APP_FEEDBACK_REPO_NAME?: string;
      REACT_APP_AUTOMATION_OUTLOOK_REPORT_PROCESS: string;
      REACT_APP_AUTOMATION_COURSES_PROCESS: string;
      REACT_APP_AUTOMATION_INSTRUCTORS_PROCESS: string;
      REACT_APP_AUTOMATION_CERTIFICATION_PROCESS: string;
      REACT_APP_AUTOMATION_PRIVATE_EVENTS_PROCESS: string;
      REACT_APP_AUTOMATION_AD_HOC_EVENTS_PROCESS: string; 
      REACT_APP_AUTOMATION_PIPELINE_EVENTS_PROCESS: string;
      REACT_APP_AUTOMATION_SURVEY_PROCESS: string;
      REACT_APP_GLOBAL_FILTER_ALLOWED_ROLES?: string; // Comma-separated list of roles allowed to create global filters
    };
  }
}

export const COMMIT_HASH = window._env_?.REACT_APP_COMMIT_HASH || process.env.REACT_APP_COMMIT_HASH || 'N/A';
export const APP_VERSION = window._env_?.REACT_APP_VERSION || process.env.REACT_APP_VERSION || 'Local Dev';
export const BUILD_DATE = window._env_?.REACT_APP_BUILD_DATE || process.env.REACT_APP_BUILD_DATE || new Date();

export const ALLOCATION_SERVICE_HOST = window._env_?.REACT_APP_ALLOCATION_SERVICE_HOST || 'http://localhost:8080';

export const BASE_CERTIFICATION_VERIFY_URL = window._env_?.REACT_APP_BASE_CERTIFICATION_VERIFY_URL || 'https://rhtapps.redhat.com/verify/?certId=';

export const KEYCLOAK_URL = window._env_?.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081';
export const KEYCLOAK_REALM = window._env_?.REACT_APP_KEYCLOAK_REALM || 'cas';
export const KEYCLOAK_CLIENT_ID = window._env_?.REACT_APP_KEYCLOAK_CLIENT_ID || 'cas-test';

export const FEEDBACK_ENABLED = window._env_?.REACT_APP_FEEDBACK_ENABLED || 'false';
export const FEEDBACK_REPO_OWNER = window._env_?.REACT_APP_FEEDBACK_REPO_OWNER || '';
export const FEEDBACK_REPO_NAME = window._env_?.REACT_APP_FEEDBACK_REPO_NAME || '';

// Automation Process 
export const AUTOMATION_OUTLOOK_REPORT_PROCESS = window._env_?.REACT_APP_AUTOMATION_OUTLOOK_REPORT_PROCESS || 'casOutlookReportProcessor';
export const AUTOMATION_COURSES_PROCESS = window._env_?.REACT_APP_AUTOMATION_COURSES_PROCESS || 'courseAndExamProcessor';
export const AUTOMATION_INSTRUCTORS_PROCESS = window._env_?.REACT_APP_AUTOMATION_INSTRUCTORS_PROCESS || 'instructorProcessor';
export const AUTOMATION_CERTIFICATION_PROCESS = window._env_?.REACT_APP_AUTOMATION_CERTIFICATION_PROCESS || 'certificationSkillUpdate';
export const AUTOMATION_PRIVATE_EVENTS_PROCESS = window._env_?.REACT_APP_AUTOMATION_PRIVATE_EVENTS_PROCESS || 'privateEventsProcessor';
export const AUTOMATION_AD_HOC_EVENTS_PROCESS = window._env_?.REACT_APP_AUTOMATION_AD_HOC_EVENTS_PROCESS || 'adHocEventsProcessor';
export const AUTOMATION_PIPELINE_EVENTS_PROCESS = window._env_?.REACT_APP_AUTOMATION_PIPELINE_EVENTS_PROCESS || 'pipelineEventsProcessor';
// Placeholder default: the automation system's own name for this process is still to be confirmed,
// and until it is, deployments set REACT_APP_AUTOMATION_SURVEY_PROCESS to whatever it turns out to be.
export const AUTOMATION_SURVEY_PROCESS = window._env_?.REACT_APP_AUTOMATION_SURVEY_PROCESS || 'casSurveyFiles';

/**
 * Roles allowed to create, edit and delete global saved filters, for when the service cannot be
 * asked.
 *
 * No longer the authority: the service decides this, in its SAVED_FILTERS_GLOBAL_EDITION parameter,
 * because it is the side that *enforces* it in SavedFilterResource.canGlobalEdit. Read it with
 * `useParameterStringList(PARAMETER.SAVED_FILTERS_GLOBAL_EDITION, GLOBAL_FILTER_ALLOWED_ROLES)`.
 *
 * Setting the environment variable therefore no longer widens who may edit a global filter on a
 * reachable service - it only decides what this application assumes until it has been told. Change
 * the parameter instead, in Administration > General Settings.
 */
export const GLOBAL_FILTER_ALLOWED_ROLES = window._env_?.REACT_APP_GLOBAL_FILTER_ALLOWED_ROLES?.split(',') || ['admin'];

/**
 * How far back a class stays live, in whole weeks counted from the Sunday that opens the
 * current week. Governs two things that have to agree:
 *  - the window the class store fetches and keeps (KlassApi.getAll), which is what the
 *    Classes list and the Calendar show;
 *  - the period within which a class may still be edited (isKlassEditable).
 * Anything older is history: reachable through Classes History, read only wherever it is
 * opened. Raising this widens both at once.
 */
export const CLASS_EDIT_PERIOD_WEEKS = 4;

/* ------------------------------------------------------------------------------------------
 * Request sizes
 *
 * How much this application asks for in one go. They are all deliberately generous: the stores
 * are built to load everything they need to work with in as few requests as possible and then
 * filter in the browser, so these are "comfortably more than there will ever be" numbers rather
 * than paging sizes. Nothing here pages - a store makes one request and keeps the answer.
 *
 * That trade is what to understand before changing one. It buys a responsive UI and a quiet API,
 * and it costs a ceiling: if a collection ever outgrows its size, the endpoint returns the first
 * N rows and the store keeps them without knowing the rest exist. The list is simply missing its
 * tail, with no error anywhere.
 *
 * Which is why nobody has to remember to watch these. Once per session the application counts
 * every one of these collections and compares it with the size below, and says so in a banner if
 * one of them no longer fits - see store/dataCompletenessStore.ts. Adding a store means adding a
 * line to the list there, or it goes unwatched.
 *
 * Occupancy measured against the live service on 2026-08-11, tightest first; everything not
 * listed had at least 6x room:
 *
 *   collection              rows    size   headroom
 *   holiday                3,201  10,000       3.1x   grows ~1,600/yr, see below
 *   course                   288   1,000       3.5x
 *   country                  251   1,000       4.0x
 *   instructor               248   1,000       4.0x
 *   language                 184   1,000       5.4x
 *   class window             934   5,000       5.4x
 *
 * One of them has since gone past its size rather than merely approached it: automation_message
 * held 1,711 rows against ENTITY_STORE_PAGE_SIZE's 1,000 on 2026-08-24 - counted in the local
 * database rather than the live service, so read it as an order of magnitude - so the Automation
 * Messages page was showing well under half of a list. It names its own size now; see
 * AUTOMATION_MESSAGE_STORE_PAGE_SIZE.
 *
 * When one does get close, there are three options in increasing order of effort: raise the number
 * here; turn it into a runtime parameter on the service so it can be raised without a release; or
 * make the loader page until the endpoint returns an empty page, which removes the ceiling
 * altogether. Nothing needs the third today.
 * ------------------------------------------------------------------------------------------ */

/**
 * Rows an entity store asks for. Applies to every collection that has not named its own size -
 * countries, courses, instructors, languages, partners and the rest of the reference data.
 */
export const ENTITY_STORE_PAGE_SIZE = 1000;

/**
 * Rows the class store asks for. Its own size because the window it holds is far larger than any
 * of the reference collections.
 *
 * Must stay at or below the service's SEARCH_MAX_PAGE_SIZE, which is the most GET /class/search
 * will return for any one request: asking for more is not an error, the endpoint silently clamps
 * it, so exceeding it would cost the tail of the window without saying so. That setting is now
 * runtime-adjustable from Administration > General Settings, so treat this as its floor -
 * lowering the parameter below this number is what would truncate the Classes list and the
 * Calendar.
 */
export const KLASS_STORE_PAGE_SIZE = 5000;

/**
 * Rows the holiday store asks for, raised off ENTITY_STORE_PAGE_SIZE because it had already
 * outgrown it: 3,201 holidays existed against a size of 1,000, so the Holidays administration page
 * was showing two thirds of a list, and its country and year filters - which it builds from the
 * rows it managed to load - were short of options to match.
 *
 * Counts the nationwide days only, which is what the holiday endpoints answer with unless a
 * `region` is asked for. See HOLIDAY_ALL_REGIONS_STORE_PAGE_SIZE for the administration screen,
 * which asks for every subdivision's as well and is several times this size.
 *
 * The one collection where a fixed size structurally loses. It grows by roughly 1,600 rows a year
 * (1,602 dated 2026, 1,599 dated 2027) whatever the business does, because it is one row per
 * holiday per country per year, so this buys about four years rather than settling it. The durable
 * fix is to stop asking for all of them: the Calendar already fetches per country and year through
 * HolidayApi.getByCountryAndYear and does not need the bulk load at all, which leaves the
 * administration page as the only caller that wants a broad list, and a year filter there would
 * bound it permanently.
 */
export const HOLIDAY_STORE_PAGE_SIZE = 10_000;

/**
 * Rows the Holidays administration screen asks for. Its own size because it is the one view that
 * asks for `?region=all` - it manages holidays, so it has to show the regional ones it can create
 * - and that collection is several times the nationwide one: a country publishes a national day
 * plus one row per subdivision that observes its own, and Spain's regional days land on dates
 * Spain already has a national holiday on.
 *
 * Sized from the nationwide 3,201 at the five-fold the service warned regions would cost, which
 * is about 16,000 today against growth of maybe 8,000 a year - three years or so. Deliberately
 * generous rather than tight: this is the size at which the page silently loses the tail of a
 * list, and an administration screen missing holidays it is meant to be managing is worse than a
 * request that takes another moment.
 *
 * Watched like the rest - dataCompletenessStore counts it with the same `region=all` the store
 * loads with, because a count taken without it measures a different collection entirely and would
 * report the page as truncated on every sign-in.
 */
export const HOLIDAY_ALL_REGIONS_STORE_PAGE_SIZE = 40_000;

/**
 * Rows the automation message store asks for. Its own size, at the class window's level rather than
 * ENTITY_STORE_PAGE_SIZE's, because this collection is a log and not reference data: every
 * automation run appends to it, so it grows with how long the service has been running rather than
 * with the business, and ENTITY_STORE_PAGE_SIZE was cutting the list short.
 *
 * A bigger number is a reprieve rather than a settlement for exactly that reason. What would settle
 * it is bounding the page by date, or a retention policy on the service, so the list stops depending
 * on a constant here; until then the completeness banner is what says when 5,000 has been outgrown
 * too.
 *
 * Costs nothing at startup: the Automation Messages page is the store's only consumer, so the rows
 * are fetched when somebody opens it and dropped again by the store's TTL once they leave.
 */
export const AUTOMATION_MESSAGE_STORE_PAGE_SIZE = 5000;

/**
 * Rows the Calendar asks for when it fetches a single week directly, bypassing the store.
 *
 * Much smaller than the window size because it covers seven days rather than months - the
 * busiest week measured held 40 classes.
 */
export const CALENDAR_WEEK_PAGE_SIZE = 1000;

/**
 * Notifications the bell panel asks for at a time.
 *
 * The one size here that is a paging size rather than a ceiling: the notification inbox is the only
 * collection this application pages through, because it grows without bound and nothing needs all of
 * it at once. The panel loads this many, and asks for the next page when the user scrolls to the
 * end, so there is no truncation to watch for and it is deliberately absent from the completeness
 * check in store/dataCompletenessStore.ts.
 *
 * Must stay at or below the service's NOTIFICATION_MAX_PAGE_SIZE (default 200), which silently
 * clamps anything larger.
 */
export const NOTIFICATION_PAGE_SIZE = 25;

/**
 * Revisions one page of an entity's change log asks for.
 *
 * A paging size, like the notifications above and unlike every other size here: an audit trail
 * grows without bound and a reader wants the newest few. The service defaults to the same 25 and
 * clamps anything above HISTORY_MAX_PAGE_SIZE rather than refusing it, so this is what we ask for
 * and not a ceiling we are held to.
 */
export const HISTORY_PAGE_SIZE = 25;

/**
 * The most GET /history/{entity}/{id} and GET /history/feed will return for one request.
 *
 * A mirror of the service's contract rather than a choice of ours - a constant on its side too,
 * deliberately not a runtime parameter, because a page of revisions costs a diff over every
 * audited field and the caller cannot see that cost. Anything larger is clamped silently, so a
 * picker offering sizes must not offer more than this.
 */
export const HISTORY_MAX_PAGE_SIZE = 200;

/**
 * The most GET /class/search will return for one request, for when the service cannot be asked.
 *
 * A mirror of the service's contract rather than a choice of ours, which is what separates it
 * from the sizes above: they are how much we want, this is how much we are allowed. The
 * live value is the SEARCH_MAX_PAGE_SIZE parameter - read it with
 * `parameterInt(PARAMETER.SEARCH_MAX_PAGE_SIZE, KLASS_SEARCH_MAX_PAGE_SIZE)` - and this constant
 * carries the value the code assumed before it could ask.
 */
export const KLASS_SEARCH_MAX_PAGE_SIZE = 5000;


export interface StatusDisplayConfig {
  bg: string;
  text: string;
  pfColor?: LabelProps['color'];
  pfVariant?: LabelProps['variant'];
}

// `bg`/`text` mirror how PatternFly builds its own filled Labels: a
// `nonstatus--<color>--default` fill paired with the matching
// `text--color--nonstatus--on-<color>--default` foreground. Both halves of that
// pair are redefined by the dark theme, so the pair stays legible either way —
// which is why these must be tokens and not the hex values they fall back to.
const nonStatus = (color: string, bgFallback: string, textFallback: string): Pick<StatusDisplayConfig, 'bg' | 'text'> => ({
  bg: `var(--pf-t--global--color--nonstatus--${color}--default, ${bgFallback})`,
  text: `var(--pf-t--global--text--color--nonstatus--on-${color}--default, ${textFallback})`,
});

// Renamed to reflect global, generic usage
export const ClassStatusDisplayConfig: Record<string, StatusDisplayConfig> = {
  // Green mappings
  [KLASS_STATUS.CONFIRMED]: { ...nonStatus('green', '#e4f5e1', '#1e4f18'), pfColor: 'green' },
  [KLASS_STATUS.GUARANTEED_TO_RUN]: { ...nonStatus('green', '#e4f5e1', '#1e4f18'), pfColor: 'green' },

  // Blue mappings
  [KLASS_STATUS.SCHEDULED]: { ...nonStatus('blue', '#e7f1fa', '#004080'), pfColor: 'blue' },

  // Yellow mappings
  [KLASS_STATUS.TENTATIVE]: { ...nonStatus('yellow', '#fdf7e3', '#795600'), pfColor: 'yellow' },
  [KLASS_STATUS.PENDING]: { ...nonStatus('yellow', '#fdf7e3', '#795600'), pfColor: 'yellow' },

  // Purple mappings
  [KLASS_STATUS.PIPELINE]: { ...nonStatus('purple', '#f2e0fa', '#401959'), pfColor: 'purple' },

  // Orange mappings
  [KLASS_STATUS.ON_HOLD]: { ...nonStatus('orange', '#fff6ec', '#8f4700'), pfColor: 'orange' },
  [KLASS_STATUS.MOVED]: { ...nonStatus('orange', '#fff6ec', '#8f4700'), pfColor: 'orange' },

  // Grey / Outline mappings
  [KLASS_STATUS.CANCELLED]: { ...nonStatus('gray', '#f0f0f0', '#6a6e73'), pfColor: 'grey', pfVariant: 'outline' },
  // Delivered keeps its plain-surface look rather than a coloured fill, so it
  // reads as "outline" next to Confirmed's solid green.
  [KLASS_STATUS.DELIVERED]: {
    bg: 'var(--pf-t--global--background--color--primary--default, #ffffff)',
    text: 'var(--pf-t--global--text--color--status--success--default, #1e4f18)',
    pfColor: 'green',
    pfVariant: 'outline',
  },

  // Fallback
  DEFAULT: { ...nonStatus('gray', '#f0f0f0', '#151515'), pfColor: 'grey' },
};